import { useEffect, useState } from 'react';
import { AlertTriangle, Copy, ExternalLink, Loader2, ShieldCheck, ShieldX, Sparkles, X } from 'lucide-react';
import toast from 'react-hot-toast';
import type { SongAnalysis } from '../../services/songAnalysis';
import { getSongVerdict } from '../../services/songReviews';

export type SongVerdict = 'passed' | 'blocked';

interface SongAnalysisModalProps {
  title: string;
  artist: string;
  onClose: () => void;
  analyze: (title: string, artist: string) => Promise<SongAnalysis>;
  reanalyze: (title: string, artist: string, lyrics: string) => Promise<SongAnalysis>;
  setVerdict: (title: string, artist: string, verdict: SongVerdict | null) => Promise<void>;
}

const PROFANITY_BADGE: Record<SongAnalysis['profanityLevel'], string> = {
  none: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  mild: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  moderate: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  heavy: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

function profanityLabel(level: SongAnalysis['profanityLevel']): string {
  return level === 'none' ? 'Clean' : `${level.charAt(0).toUpperCase()}${level.slice(1)} Profanity`;
}

function lyricsSourceText(source: SongAnalysis['lyricsSource']): string {
  if (source === 'web_search') return 'Analyzed with song lyrics · Results are cached';
  if (source === 'manual') return 'Analyzed with pasted lyrics · Results are cached';
  return 'Analyzed from AI knowledge (no lyrics found) · Results are cached';
}

// Analysis, lyrics fact-check and Pass/Block, built from PendingPage's inline modal (ITER-194)
export function SongAnalysisModal({ title, artist, onClose, analyze, reanalyze, setVerdict }: SongAnalysisModalProps) {
  const [analysis, setAnalysis] = useState<SongAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lyrics, setLyrics] = useState('');
  const [reanalyzing, setReanalyzing] = useState(false);
  const [verdict, setVerdictState] = useState<SongVerdict | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    analyze(title, artist)
      .then(result => { if (!cancelled) setAnalysis(result); })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to analyze song');
        toast.error('Failed to analyze song');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    getSongVerdict(title, artist)
      .then(saved => { if (!cancelled) setVerdictState(saved); })
      .catch((err: unknown) => console.warn('[SongAnalysisModal] Could not read the saved verdict:', err));
    return () => { cancelled = true; };
  }, [title, artist, analyze]);

  const handleReanalyze = async () => {
    if (!lyrics.trim()) return;
    setReanalyzing(true);
    try {
      setAnalysis(await reanalyze(title, artist, lyrics));
      setLyrics('');
      toast.success('Re-analyzed with provided lyrics');
    } catch {
      toast.error('Failed to re-analyze with lyrics');
    } finally {
      setReanalyzing(false);
    }
  };

  const handleVerdict = async (next: SongVerdict) => {
    const newVerdict = verdict === next ? null : next;
    try {
      await setVerdict(title, artist, newVerdict);
      setVerdictState(newVerdict);
      toast.success(newVerdict ? `Song ${newVerdict}` : 'Verdict cleared');
    } catch {
      toast.error('Failed to update verdict');
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500" /> AI Song Analysis
          </h3>
          <button onClick={onClose} className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="mb-3">
          <p className="font-medium text-gray-900 dark:text-white">{title}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{artist}</p>
        </div>
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 mx-auto mb-3 text-purple-500 animate-spin" />
            <p className="text-gray-500 dark:text-gray-400">Analyzing song...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-danger" />
            <p className="text-danger">{error}</p>
          </div>
        ) : analysis ? (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Summary</h4>
              <p className="text-gray-900 dark:text-white">{analysis.summary}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Profanity Analysis</h4>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mb-2 ${PROFANITY_BADGE[analysis.profanityLevel]}`}>
                {profanityLabel(analysis.profanityLevel)}
              </span>
              <p className="text-gray-900 dark:text-white text-sm">{analysis.profanityAnalysis}</p>
            </div>
            <p className={`text-xs ${
              analysis.lyricsSource === 'web_search' || analysis.lyricsSource === 'manual'
                ? 'text-emerald-500 dark:text-emerald-400'
                : 'text-amber-500 dark:text-amber-400'
            }`}>
              {lyricsSourceText(analysis.lyricsSource)}
            </p>

            {analysis.lyricsSource !== 'manual' && (
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Paste lyrics from Musixmatch for a more accurate analysis:</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${title} ${artist}`)
                        .then(() => toast.success('Copied to clipboard'))
                        .catch(() => toast.error('Could not copy'));
                    }}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                    Copy Title & Artist
                  </button>
                  <button
                    onClick={() => window.open('https://www.musixmatch.com/search', '_blank')}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-md hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Open Musixmatch
                  </button>
                </div>
                <textarea
                  value={lyrics}
                  onChange={(event) => setLyrics(event.target.value)}
                  placeholder="Paste lyrics here..."
                  rows={4}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button
                  onClick={handleReanalyze}
                  disabled={!lyrics.trim() || reanalyzing}
                  className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {reanalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {reanalyzing ? 'Re-analyzing...' : 'Re-analyze with Lyrics'}
                </button>
              </div>
            )}
          </div>
        ) : null}
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          {analysis && (
            <>
              <button
                onClick={() => handleVerdict('passed')}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  verdict === 'passed'
                    ? 'bg-green-500 text-white'
                    : 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/40'
                }`}
              >
                <ShieldCheck className="w-4 h-4" />
                {verdict === 'passed' ? 'Passed' : 'Pass'}
              </button>
              <button
                onClick={() => handleVerdict('blocked')}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  verdict === 'blocked'
                    ? 'bg-red-500 text-white'
                    : 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40'
                }`}
              >
                <ShieldX className="w-4 h-4" />
                {verdict === 'blocked' ? 'Blocked' : 'Block'}
              </button>
            </>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="btn btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
}
